import { authClient } from "@/shared/rpc/clients";
import { API_BASE_URL, ENVIRONMENT } from "@/shared/config/config";
import { maintenanceStore } from "@/shared/stores/MaintenanceStore";
import { ROUTES } from "@/shared/config/routes";
import { supportedLocales } from "@/shared/lib/i18n/config";
import { detectLocaleByNavigator } from "@/shared/lib/i18n/countryLocaleMap";
import { localeStorage } from "@/shared/lib/locale-storage";
import { userStore } from "@/shared/stores/UserStore";
import { geoStore } from "@/shared/stores/GeoStore";
import { CircularLoading } from "@/shared/ui/CircularLoading";
import { RESTRICTED_COUNTRIES } from "@cronika/toolkit";
import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const BYPASS_KEY = "maintenance_bypass";

// Re-exported here for backwards compatibility with call sites that import
// `RESTRICTED_COUNTRIES` from this guard. Source of truth lives in
// `@cronika/toolkit` (shared with frontend/admin).
export { RESTRICTED_COUNTRIES };

const PUBLIC_ROUTES = [
  ROUTES.SIGN_IN,
  ROUTES.SIGN_UP,
  ROUTES.MAINTENANCE,
  ROUTES.ACCESS_DENIED,
  ROUTES.NOT_FOUND,
  ROUTES.INSTALL_APP,
  ROUTES.FORGOT_PASSWORD,
  ROUTES.RESET_PASSWORD,
  ROUTES.STATS_UNSUBSCRIBE,
  ROUTES.NEWS_UNSUBSCRIBE,
  ROUTES.RESTORE_ACCOUNT,
  ROUTES.SESSION_COUNTER_WIDGET,
  "/confirm-email",
] as const;

type AppLocale = (typeof supportedLocales)[number];

const isSupportedLocale = (value: string): value is AppLocale => {
  return (supportedLocales as readonly string[]).includes(value);
};

const getLocaleFromPath = (pathname: string): string => {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0] ?? "";
  if (isSupportedLocale(first)) return first;

  const saved = localeStorage.get();
  if (saved && isSupportedLocale(saved)) return saved;

  return detectLocaleByNavigator(supportedLocales);
};

const isPublicRoute = (pathname: string): boolean => {
  return PUBLIC_ROUTES.some((route) => pathname.includes(route));
};

interface AppGuardProps {
  children: ReactNode;
}

export const AppGuard = observer(function AppGuard({ children }: AppGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);
  const guardRanRef = useRef(false);
  const countryRestrictedRef = useRef(false);

  // ==================== INITIAL GUARDS (run once) ====================
  useEffect(() => {
    if (guardRanRef.current) return;
    guardRanRef.current = true;

    const runInitialGuards = async () => {
      let { pathname } = location;

      try {
        // 1. MAINTENANCE BYPASS
        if (pathname.startsWith("/secret/") || pathname === "/secret") {
          sessionStorage.setItem(BYPASS_KEY, "true");
          pathname = pathname.replace(/^\/secret/, "") || "/";
          navigate(pathname, { replace: true });
          // Continue with guards using the real path (don't return early)
        }

        const locale = getLocaleFromPath(pathname);
        const maintenancePath = `/${locale}${ROUTES.MAINTENANCE}`;
        const hasBypass = sessionStorage.getItem(BYPASS_KEY) === "true";

        // 2. MAINTENANCE MODE
        try {
          const res = await fetch(`${API_BASE_URL}/rest/public/maintenance`);
          if (res.ok) {
            const json = await res.json() as { maintenance: boolean };
            maintenanceStore.setEnabled(json.maintenance);
          }
        } catch {
          // network error — leave current maintenance state as-is
        }

        if (maintenanceStore.enabled && !hasBypass) {
          if (pathname !== maintenancePath) {
            navigate(maintenancePath, { replace: true });
          }
          setIsInitialized(true);
          return;
        }

        // 3. AUTHENTICATION CHECK
        let isAuthenticated = false;
        try {
          const data = await authClient.refresh({});
          userStore.setUserData(data);
          isAuthenticated = true;

          if (isPublicRoute(pathname) && (pathname.includes(ROUTES.SIGN_IN) || pathname.includes(ROUTES.SIGN_UP))) {
            navigate(`/${locale}${ROUTES.HOME}`, { replace: true });
          }

          if (pathname.includes(ROUTES.ACCESS_DENIED)) {
            navigate(`/${locale}${ROUTES.HOME}`, { replace: true });
          }
        } catch {
          if (!isPublicRoute(pathname)) {
            navigate(`/${locale}${ROUTES.SIGN_IN}`, { replace: true });
          }
        }

        // 4. COUNTRY RESTRICTION (only for unauthenticated users, skipped on DEV)
        if (false && !isAuthenticated && !pathname.includes(ROUTES.INSTALL_APP) && ENVIRONMENT !== "DEV") {
          await geoStore.fetch();

          const countryCode = geoStore.countryCode;
          if (countryCode) {
            const isRestricted = (RESTRICTED_COUNTRIES as readonly string[]).includes(countryCode ?? "");

            if (isRestricted) {
              countryRestrictedRef.current = true;
              if (!pathname.includes(ROUTES.ACCESS_DENIED)) {
                const search = window.location.search;
                navigate(`/${locale}${ROUTES.ACCESS_DENIED}${search}`, { replace: true });
              }
              setIsInitialized(true);
              return;
            }

            if (!isRestricted && pathname.includes(ROUTES.ACCESS_DENIED)) {
              const search = window.location.search;
              navigate(`/${locale}${ROUTES.SIGN_IN}${search}`, { replace: true });
            }
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error("AppGuard error:", error);
        setIsInitialized(true);
      }
    };

    runInitialGuards();
  }, []);

  // ==================== ROUTE GUARD (on navigation) ====================
  const isUserAuthenticated = !!userStore.user;

  useEffect(() => {
    if (!isInitialized) return;

    const { pathname } = location;
    const locale = getLocaleFromPath(pathname);

    if (pathname.startsWith("/secret/") || pathname === "/secret") {
      sessionStorage.setItem(BYPASS_KEY, "true");
      const realPath = pathname.replace(/^\/secret/, "") || "/";
      navigate(realPath, { replace: true });
      return;
    }

    const hasBypass = sessionStorage.getItem(BYPASS_KEY) === "true";
    if (maintenanceStore.enabled && !hasBypass && !pathname.includes(ROUTES.MAINTENANCE)) {
      navigate(`/${locale}${ROUTES.MAINTENANCE}`, { replace: true });
      return;
    }
    if (!maintenanceStore.enabled && pathname.includes(ROUTES.MAINTENANCE)) {
      navigate(`/${locale}${ROUTES.HOME}`, { replace: true });
      return;
    }

    if (isUserAuthenticated) {
      countryRestrictedRef.current = false;
    }

    if (countryRestrictedRef.current) {
      if (!pathname.includes(ROUTES.ACCESS_DENIED)) {
        navigate(`/${locale}${ROUTES.ACCESS_DENIED}`, { replace: true });
      }
      return;
    }

    if (!isUserAuthenticated && !isPublicRoute(pathname)) {
      navigate(`/${locale}${ROUTES.SIGN_IN}`, { replace: true });
    }
  }, [location.pathname, isInitialized, isUserAuthenticated, navigate]);

  if (!isInitialized) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <CircularLoading />
      </div>
    );
  }

  return <>{children}</>;
});
